import React, { FC } from "react";
import styled from "@emotion/styled";
import { FormattedMessage } from "react-intl";
import { Pill, PillColors, ThemeProps } from "common/components/ui";
import { StyledText, StyledInfoIcon, ConfirmationDetailsBox, ConfirmationDetailsValue } from "../sharedComponents";
import { commonPalletDiscrepancyLabels } from "./CommonPalletDiscrepancy.labels";

const DiscrepancyPill = styled(Pill)<ThemeProps>(
  ({ theme }) => `
    display: inline-flex;
    margin-bottom: ${theme.spacing.S4};
    padding: ${theme.spacing.S1} ${theme.spacing.S2};
  `
);

const ShipmentContentValue = styled(ConfirmationDetailsValue)`
  margin-top: 0;
`;

const BoxesText = styled(StyledText)<ThemeProps>(
  ({ theme }) => `
    margin-top: ${theme.spacing.S4};
    color: ${theme.colors.NEUTRAL[400]};
  `
);

const BoxesOnlyValue = styled(ConfirmationDetailsValue)`
  margin-top: 0;
`;

interface CommonPalletDiscrepancyProps {
  expectedPallets: number | null;
  actualPallets: number | null;
  expectedBoxes: number | null;
  shipmentContentPallets?: number | null;
  className?: string;
}

export const CommonPalletDiscrepancy: FC<CommonPalletDiscrepancyProps> = ({
  expectedPallets,
  actualPallets,
  expectedBoxes,
  shipmentContentPallets,
  className,
}) => {
  const hasDiscrepancy = actualPallets !== null && expectedPallets !== null && actualPallets !== expectedPallets;
  const palletCount = actualPallets !== null ? actualPallets : expectedPallets ?? shipmentContentPallets ?? 0;
  const hasOnlyBoxes = expectedBoxes !== null && expectedPallets === null && actualPallets === null;

  return (
    <ConfirmationDetailsBox className={className}>
      {hasDiscrepancy && (
        <DiscrepancyPill color={PillColors.RED}>
          <FormattedMessage {...commonPalletDiscrepancyLabels.palletDiscrepancy} />
        </DiscrepancyPill>
      )}
      <StyledText size="bodyLarge">
        <FormattedMessage {...commonPalletDiscrepancyLabels.shipmentContents} />
        {!hasOnlyBoxes && (
          <StyledInfoIcon message={<FormattedMessage {...commonPalletDiscrepancyLabels.shipmentContentsTooltip} />} />
        )}
      </StyledText>
      {!hasOnlyBoxes && <ShipmentContentValue displayAs="h2">{palletCount} pallets</ShipmentContentValue>}
      {expectedBoxes !== null &&
        (hasOnlyBoxes ? (
          <BoxesOnlyValue displayAs="h2">
            <FormattedMessage {...commonPalletDiscrepancyLabels.boxes} values={{ count: expectedBoxes }} />
          </BoxesOnlyValue>
        ) : (
          <BoxesText size="body">
            <FormattedMessage {...commonPalletDiscrepancyLabels.expectedBoxes} values={{ count: expectedBoxes }} />
          </BoxesText>
        ))}
    </ConfirmationDetailsBox>
  );
};
